import type { SyntaxPreset, SyntaxPresetName } from "./merge-tags";

export type ViewportSize = "desktop" | "mobile";

export type UiTheme = "light" | "dark" | "auto";

export interface CustomFont {
  name: string;
  url: string;
  fallback?: string;
}

export interface FontsConfig {
  defaultFallback?: string;
  defaultFont?: string;
  customFonts?: CustomFont[];
  /**
   * Restrict which of the seven built-in fonts the font picker offers.
   *
   * - `true` / omitted — every built-in is offered (the default).
   * - `false` — no built-ins; the picker lists only `customFonts`. Use this to
   *   keep authors on an embedded brand kit's typefaces.
   * - `string[]` — an allowlist of built-in names to keep, matched
   *   case-insensitively (`['Georgia', 'arial']`). A name that isn't a built-in
   *   is logged with a warning and skipped — consistent with how
   *   `paletteBlocks` treats an unknown entry — so a typo narrows the list
   *   rather than silently offering all seven.
   *
   * Independent of `customFonts`: excluding the built-ins never removes a custom
   * font, and a custom font can still be the `defaultFont` when every built-in
   * is excluded. Filtering the picker never affects rendering — content already
   * using an excluded built-in still resolves to its proper fallback stack.
   */
  builtIns?: boolean | string[];
}

export interface ColorsConfig {
  /**
   * Preset colors rendered as a clickable grid inside every color picker
   * popover. Each entry is a hex string (`'#0b5cff'`); clicking one applies it,
   * and the preset matching the current value is marked selected (matched
   * case-insensitively). The grid supplements the wheel and hex input unless
   * `allowCustom` is `false`.
   */
  presets?: string[];
  /**
   * Allow free-form color entry — the wheel and hex input — alongside the
   * presets. Defaults to `true`.
   *
   * Set to `false` together with `presets` to lock authors to the preset
   * palette: the popover then offers only the preset grid, with no wheel or hex
   * field (a white-label / brand-kit constraint). Ignored with a warning when
   * no `presets` are configured — hiding the only inputs would leave the picker
   * with no way to choose a color.
   */
  allowCustom?: boolean;
}

export interface ExportResult {
  html: string;
  mjml: string;
}

export interface MergeTag {
  label: string;
  value: string;
  /**
   * Optional grouping label used by the built-in merge tag picker to
   * section the list. When no tag in the configured array carries
   * `group`, the picker renders a plain flat list with no headers.
   * Ignored by the renderer and by typing-autocomplete.
   */
  group?: string;
  /**
   * Optional helper text shown beneath the tag in the built-in merge
   * tag picker. Not rendered anywhere else (toolbar, autocomplete,
   * MJML output) and not stored on the inserted document node.
   */
  description?: string;
  /**
   * Example value shown in place of this tag on preview surfaces — e.g.
   * `"Ada"` for `{{first_name}}` — so a preview reads like a delivered
   * email instead of a list of field names.
   *
   * Display-only, and only in the previews' Sample mode: never written to
   * the template, never sent, never present in MJML output. Setting it is
   * the whole opt-in; there is no accompanying flag. A tag without a
   * sample keeps showing its `label`.
   */
  sample?: string;
}

export interface MediaResult {
  url: string;
  alt?: string;
}

export interface MergeTagsConfig {
  syntax?: SyntaxPresetName | SyntaxPreset;
  tags?: MergeTag[];
  onRequest?: () => Promise<MergeTag | null>;
  /**
   * Enables typing-based autocomplete in rich text blocks and in every
   * merge-tag-enabled input/textarea field (toolbars, template settings,
   * custom-block text fields). When the user types the syntax opener
   * (e.g. `{{`), a popup lists matching `tags`. Both surfaces share the
   * same popup, so behavior is identical.
   *
   * Defaults to `true`. Effective only when `tags` is non-empty AND
   * `syntax` matches a built-in preset (custom regex syntaxes cannot be
   * mapped to a trigger string and silently disable autocomplete).
   */
  autocomplete?: boolean;
}

/**
 * A standalone logic tag — a control-flow token (e.g. `{% else %}`) inserted
 * at the cursor. Distinct from data merge tags; rendered as a keyword badge.
 */
export interface LogicTag {
  label: string;
  value: string;
  /** Optional grouping label used to section the logic picker. */
  group?: string;
  /** Optional helper text shown beneath the tag in the picker. */
  description?: string;
}

/**
 * A paired logic construct — an opening tag (`before`) and its matching closer
 * (`after`), e.g. `{% if vip %}` … `{% endif %}`. Selecting one inserts both
 * pills at once, wrapping the selection (or with the caret placed between
 * them). Shares its `before`/`after` shape with `DisplayCondition`.
 */
export interface LogicPair {
  label: string;
  /** Opening logic tag, including delimiters (e.g. `{% if vip %}`). */
  before: string;
  /** Closing logic tag, including delimiters (e.g. `{% endif %}`). */
  after: string;
  /** Optional grouping label used to section the logic picker. */
  group?: string;
  /** Optional helper text shown beneath the pair in the picker. */
  description?: string;
}

/**
 * Standalone logic-tag configuration, independent of `mergeTags`. `tags` are
 * single control-flow tokens inserted at the cursor; `pairs` are open/close
 * constructs inserted around the selection. Both appear in the built-in logic
 * picker, grouped together by `group`. Typed/pasted logic highlighting is
 * always on regardless of this.
 */
export interface LogicTagsConfig {
  tags?: LogicTag[];
  pairs?: LogicPair[];
  /**
   * Consumer-owned picker. When set, the "Insert logic" affordance calls this
   * instead of the built-in logic picker — return the chosen tag/pair (or
   * `null` to cancel). Mirrors `MergeTagsConfig.onRequest`; lets consumers use
   * their own UI and custom syntax. Precedence: `onRequest` → built-in picker.
   */
  onRequest?: () => Promise<LogicTag | LogicPair | null>;
}

export interface DisplayCondition {
  label: string;
  before: string;
  after: string;
  group?: string;
  description?: string;
}

export interface DisplayConditionsConfig {
  conditions: DisplayCondition[];
  allowCustom?: boolean;
}

export interface ThemeOverrides {
  bg?: string;
  bgElevated?: string;
  bgHover?: string;
  bgActive?: string;
  border?: string;
  borderLight?: string;
  text?: string;
  textMuted?: string;
  textDim?: string;
  primary?: string;
  primaryHover?: string;
  primaryLight?: string;
  secondary?: string;
  secondaryHover?: string;
  secondaryLight?: string;
  success?: string;
  successLight?: string;
  warning?: string;
  warningLight?: string;
  danger?: string;
  dangerLight?: string;
  canvasBg?: string;
  dark?: Omit<ThemeOverrides, "dark">;
}

export class SdkError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
  ) {
    super(message);
    this.name = "SdkError";
  }

  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  get isServerError(): boolean {
    return this.statusCode !== undefined && this.statusCode >= 500;
  }
}
